import { SplashScreen } from '@capacitor/splash-screen';
import { useSplashScreenState } from '@helios/frontend-core';
import { Themes } from '@palmetto/dsm-tokens';
import { Alignment, Fit, Layout, useRive } from '@rive-app/react-canvas';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import logoScriptRiv from '../../../assets/logo-script.riv';
import { PlatformConfig } from '../../../config';
import { waitForInitialLiveUpdateGate } from '../../../utils/initialLiveUpdateSync';
import { useThemeContext } from '../ThemeProvider/ThemeProvider';

const LIVE_UPDATE_GATE_TIMEOUT_MS = 3000;

interface AnimatedSplashScreenProps {
  onReleased?: () => void;
}

export const AnimatedSplashScreen: React.FC<AnimatedSplashScreenProps> = ({ onReleased }) => {
  const [visible, setVisible] = useState(true);
  const [released, setReleased] = useState(false);
  const { show, hide } = useSplashScreenState();
  const { theme } = useThemeContext();
  const onReleasedRef = useRef(onReleased);
  onReleasedRef.current = onReleased;
  const { RiveComponent, rive } = useRive(
    {
      src: logoScriptRiv,
      artboard: theme.cssClass === 'dark' ? 'Red' : 'White',
      stateMachines: 'Forward',
      autoplay: false,
      layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
    },
    { shouldResizeCanvasToContainer: true },
  );

  useEffect(() => {
    const releaseSplash = async () => {
      if (PlatformConfig.isNative) {
        await waitForInitialLiveUpdateGate(LIVE_UPDATE_GATE_TIMEOUT_MS);
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          SplashScreen.hide();
          setReleased(true);
          onReleasedRef.current?.();
        });
      });
    };
    releaseSplash();
  }, []);

  useEffect(() => {
    if (released) {
      rive?.play();
    }
  }, [released, rive]);

  useEffect(() => {
    return () => {
      rive?.cleanup();
    };
  }, [rive]);

  useEffect(() => {
    if (!PlatformConfig.isNative) return;
    show();
    return () => {
      hide();
    };
  }, [show, hide]);

  useEffect(() => {
    if (!PlatformConfig.isNative || !released) return;
    const timer = setTimeout(() => {
      setVisible(false);
      hide();
    }, 3750);
    return () => {
      clearTimeout(timer);
    };
  }, [released, hide]);

  if (!visible || !PlatformConfig.isNative) return null;

  return (
    <div
      data-theme={Themes.palmetto}
      className={classNames(
        'md:hidden bg-canvas-default py-64 px-24 absolute h-full w-full flex flex-col justify-between z-100',
        {
          '-motion-translate-y-out-100 motion-delay-[2.3s]/translate motion-duration-[300ms] motion-ease-[cubic-bezier(0.23, 1, 0.32, 1)]':
            released,
        },
      )}
    >
      <RiveComponent
        className={classNames('h-full w-full', {
          'motion-opacity-out-[0%] motion-delay-[2.2s]/opacity motion-duration-[200ms]/opacity': released,
        })}
      />
    </div>
  );
};
